/* ============================================================
   RAD BRANDS — PRIVATE LABEL PAGE
   JavaScript: Animations, Interactions, Form Logic
   ============================================================ */

(function () {
  'use strict';

  /* ── 1. SCROLL PROGRESS BAR ─────────────────────────────────── */
  function initScrollProgress() {
    const bar = document.getElementById('rpl-progress');
    if (!bar) return;
    const update = () => {
      const scrolled = window.scrollY;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.transform = 'scaleX(' + (total > 0 ? scrolled / total : 0) + ')';
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── 2. INTERSECTION OBSERVER — SCROLL ANIMATIONS ───────────── */
  function initAnimations() {
    const items = document.querySelectorAll('.anim');
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = el.dataset.delay || 0;
            setTimeout(() => el.classList.add('visible'), Number(delay));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    items.forEach((el) => observer.observe(el));
  }

  /* ── 3. STICKY NAV ──────────────────────────────────────────── */
  function initStickyNav() {
    const nav = document.getElementById('rpl-nav');
    if (!nav) return;

    let lastY = 0;
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const y = window.scrollY;
          if (y > 80) {
            nav.classList.add('scrolled');
          } else {
            nav.classList.remove('scrolled');
          }
          // Hide nav on scroll down, show on scroll up
          if (y > lastY + 8 && y > 200) {
            nav.classList.add('hidden');
          } else if (y < lastY - 4) {
            nav.classList.remove('hidden');
          }
          lastY = y;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    // Active link highlighting
    const sections = document.querySelectorAll('[data-section]');
    const navLinks = nav.querySelectorAll('.rpl-nav__link');

    if (sections.length && navLinks.length) {
      const sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = entry.target.dataset.section;
              navLinks.forEach((link) => {
                link.classList.toggle('active', link.dataset.target === id);
              });
            }
          });
        },
        { threshold: 0.3 }
      );
      sections.forEach((s) => sectionObserver.observe(s));
    }

    // Smooth scroll on nav click
    navLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        const target = link.dataset.target;
        if (!target) return;
        const el = document.getElementById(target);
        if (!el) return;
        e.preventDefault();
        closeMobileNav();
        const offset = nav.offsetHeight + 8;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }

  /* ── 4. MOBILE NAV DRAWER ───────────────────────────────────── */
  function initMobileNav() {
    const toggle = document.getElementById('rpl-nav-toggle');
    const drawer = document.getElementById('rpl-nav-drawer');
    const overlay = document.getElementById('rpl-nav-overlay');
    const close = document.getElementById('rpl-nav-close');
    if (!toggle || !drawer) return;

    toggle.addEventListener('click', openMobileNav);
    if (close) close.addEventListener('click', closeMobileNav);
    if (overlay) overlay.addEventListener('click', closeMobileNav);

    // Close on drawer link click
    drawer.querySelectorAll('a').forEach((a) => {
      a.addEventListener('click', closeMobileNav);
    });
  }

  function openMobileNav() {
    const drawer = document.getElementById('rpl-nav-drawer');
    const overlay = document.getElementById('rpl-nav-overlay');
    if (drawer) drawer.classList.add('open');
    if (overlay) overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closeMobileNav() {
    const drawer = document.getElementById('rpl-nav-drawer');
    const overlay = document.getElementById('rpl-nav-overlay');
    if (drawer) drawer.classList.remove('open');
    if (overlay) overlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  /* ── 5. FAQ ACCORDION ───────────────────────────────────────── */
  function initFAQ() {
    const items = document.querySelectorAll('.faq__item');
    if (!items.length) return;

    items.forEach((item) => {
      const trigger = item.querySelector('.faq__question');
      const body = item.querySelector('.faq__answer');
      if (!trigger || !body) return;

      trigger.addEventListener('click', () => {
        const isOpen = item.classList.contains('open');

        // Close all others
        items.forEach((other) => {
          if (other !== item) {
            other.classList.remove('open');
            const otherBody = other.querySelector('.faq__answer');
            if (otherBody) otherBody.style.maxHeight = null;
          }
        });

        // Toggle current
        if (isOpen) {
          item.classList.remove('open');
          body.style.maxHeight = null;
        } else {
          item.classList.add('open');
          body.style.maxHeight = body.scrollHeight + 'px';
        }
      });
    });
  }

  /* ── 6. PRODUCT FORMAT CAROUSEL (DRAG TO SCROLL) ────────────── */
  function initCarousel() {
    const tracks = document.querySelectorAll('.carousel-track');
    if (!tracks.length) return;

    tracks.forEach((track) => {
      let isDown = false;
      let startX;
      let scrollLeft;

      track.addEventListener('mousedown', (e) => {
        isDown = true;
        track.classList.add('dragging');
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
      });

      track.addEventListener('mouseleave', () => {
        isDown = false;
        track.classList.remove('dragging');
      });

      track.addEventListener('mouseup', () => {
        isDown = false;
        track.classList.remove('dragging');
      });

      track.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - track.offsetLeft;
        const walk = (x - startX) * 1.5;
        track.scrollLeft = scrollLeft - walk;
      });

      // Touch support
      let touchStartX = 0;
      let touchScrollLeft = 0;

      track.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].pageX;
        touchScrollLeft = track.scrollLeft;
      }, { passive: true });

      track.addEventListener('touchmove', (e) => {
        const dx = touchStartX - e.touches[0].pageX;
        track.scrollLeft = touchScrollLeft + dx;
      }, { passive: true });
    });

    // Arrow buttons
    document.querySelectorAll('.carousel-prev, .carousel-next').forEach((btn) => {
      btn.addEventListener('click', () => {
        const track = btn.closest('.carousel-wrap')?.querySelector('.carousel-track');
        if (!track) return;
        const step = track.offsetWidth * 0.75;
        track.scrollBy({
          left: btn.classList.contains('carousel-next') ? step : -step,
          behavior: 'smooth',
        });
      });
    });
  }

  /* ── 7. WHATSAPP ENQUIRY FORM ───────────────────────────────── */
  const WA_NUMBER = '919810977789';

  function initEnquiryForm() {
    const form = document.getElementById('rpl-enquiry-form');
    if (!form) return;

    const waBtn = document.getElementById('rpl-wa-btn');

    // Build WhatsApp message from form on input
    const buildMessage = () => {
      const name = (form.querySelector('[name="contact[name]"]')?.value || '').trim();
      const brand = (form.querySelector('[name="contact[body]"]')
        ? ''
        : (form.querySelector('[data-field="brand"]')?.value || '').trim());
      const product = (form.querySelector('[data-field="product"]')?.value || '').trim();
      const moq = (form.querySelector('[data-field="moq"]')?.value || '').trim();
      const phone = (form.querySelector('[name="contact[phone]"]')?.value || '').trim();

      let msg = 'Hi RAD Brands! I am interested in Private Label.\n\n';
      if (name) msg += `Name: ${name}\n`;
      if (phone) msg += `Phone: ${phone}\n`;
      if (brand) msg += `Brand: ${brand}\n`;
      if (product) msg += `Product Interest: ${product}\n`;
      if (moq) msg += `MOQ Range: ${moq}\n`;
      msg += '\nPlease get in touch.';
      return encodeURIComponent(msg);
    };

    if (waBtn) {
      waBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const msg = buildMessage();
        window.open(`https://wa.me/${WA_NUMBER}?text=${msg}`, '_blank');
      });
    }

    // On form submit, also open WhatsApp
    form.addEventListener('submit', (e) => {
      // Let Shopify contact form submit normally
      // Additionally open WhatsApp
      const msg = buildMessage();
      setTimeout(() => {
        window.open(`https://wa.me/${WA_NUMBER}?text=${msg}`, '_blank');
      }, 300);
    });
  }

  /* ── 8. STICKY BOTTOM BAR (MOBILE) ─────────────────────────── */
  function initStickyBar() {
    const bar = document.getElementById('rpl-sticky-bar');
    if (!bar) return;

    // Hide bar when enquiry form is in view
    const form = document.getElementById('rpl-enquiry-form');
    if (form) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            bar.style.opacity = entry.isIntersecting ? '0' : '1';
            bar.style.pointerEvents = entry.isIntersecting ? 'none' : 'auto';
          });
        },
        { threshold: 0.2 }
      );
      observer.observe(form);
    }

    // WhatsApp click on bar
    const waBarBtn = bar.querySelector('[data-wa]');
    if (waBarBtn) {
      waBarBtn.addEventListener('click', () => {
        const msg = encodeURIComponent(
          'Hi RAD Brands! I would like to explore private label options. Please share more details.'
        );
        window.open(`https://wa.me/${WA_NUMBER}?text=${msg}`, '_blank');
      });
    }
  }

  /* ── 9. COUNTER ANIMATION (STATS SECTION) ───────────────────── */
  function initCounters() {
    const counters = document.querySelectorAll('[data-count]');
    if (!counters.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseFloat(el.dataset.count);
          const suffix = el.dataset.suffix || '';
          const duration = 1600;
          const start = performance.now();

          const tick = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // Ease out quart
            const eased = 1 - Math.pow(1 - progress, 4);
            const current = target * eased;
            el.textContent =
              (Number.isInteger(target) ? Math.round(current) : current.toFixed(1)) + suffix;
            if (progress < 1) requestAnimationFrame(tick);
          };

          requestAnimationFrame(tick);
          observer.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((c) => observer.observe(c));
  }

  /* ── 10. TESTIMONIAL AUTO-ROTATE ────────────────────────────── */
  function initTestimonials() {
    const wrap = document.querySelector('.testimonials__track');
    if (!wrap) return;
    const cards = wrap.querySelectorAll('.testimonial-card');
    if (cards.length < 2) return;

    let current = 0;
    const dots = document.querySelectorAll('.testimonials__dot');

    const goTo = (index) => {
      current = (index + cards.length) % cards.length;
      wrap.style.transform = `translateX(-${current * 100}%)`;
      dots.forEach((d, i) => d.classList.toggle('active', i === current));
    };

    // Dot clicks
    dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));

    // Prev / next
    const prev = document.querySelector('.testimonials__prev');
    const next = document.querySelector('.testimonials__next');
    if (prev) prev.addEventListener('click', () => goTo(current - 1));
    if (next) next.addEventListener('click', () => goTo(current + 1));

    // Auto-rotate every 5s
    let timer = setInterval(() => goTo(current + 1), 5000);
    wrap.addEventListener('mouseenter', () => clearInterval(timer));
    wrap.addEventListener('mouseleave', () => {
      timer = setInterval(() => goTo(current + 1), 5000);
    });

    goTo(0);
  }

  /* ── 11. PROCESS STEPS — HOVER HIGHLIGHT ────────────────────── */
  function initProcess() {
    const steps = document.querySelectorAll('.process__step');
    if (!steps.length) return;

    steps.forEach((step) => {
      step.addEventListener('mouseenter', () => {
        steps.forEach((s) => s.classList.add('dimmed'));
        step.classList.remove('dimmed');
        step.classList.add('highlighted');
      });
      step.addEventListener('mouseleave', () => {
        steps.forEach((s) => {
          s.classList.remove('dimmed');
          s.classList.remove('highlighted');
        });
      });
    });
  }

  /* ── 12. SMOOTH SCROLL FOR CTA LINKS ────────────────────────── */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        const id = anchor.getAttribute('href').slice(1);
        if (!id) return;
        const target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        const nav = document.getElementById('rpl-nav');
        const offset = nav ? nav.offsetHeight + 8 : 80;
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }

  /* ── 13. HERO PARALLAX (subtle, desktop only) ───────────────── */
  function initParallax() {
    const hero = document.querySelector('.hero');
    if (!hero || window.matchMedia('(max-width: 768px)').matches) return;

    const badge = hero.querySelector('.hero__badge');
    const visual = hero.querySelector('.hero__visual');

    window.addEventListener(
      'scroll',
      () => {
        const y = window.scrollY;
        if (badge) badge.style.transform = `translateY(${y * 0.08}px)`;
        if (visual) visual.style.transform = `translateY(${y * 0.05}px)`;
      },
      { passive: true }
    );
  }

  /* ── 14. BLOG / LEARNING LIBRARY FILTER ────────────────────── */
  function initBlogFilter() {
    const btns = document.querySelectorAll('.blog-filter__btn');
    const cards = document.querySelectorAll('.blog-card');
    if (!btns.length || !cards.length) return;

    btns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const tag = btn.dataset.tag;
        btns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');

        cards.forEach((card) => {
          const match = tag === 'all' || (card.dataset.tags || '').includes(tag);
          card.style.display = match ? '' : 'none';
          if (match) {
            card.classList.remove('visible');
            setTimeout(() => card.classList.add('visible'), 50);
          }
        });
      });
    });
  }

  /* ── INIT ────────────────────────────────────────────────────── */
  function init() {
    initScrollProgress();
    initAnimations();
    initStickyNav();
    initMobileNav();
    initFAQ();
    initCarousel();
    initEnquiryForm();
    initStickyBar();
    initCounters();
    initTestimonials();
    initProcess();
    initSmoothScroll();
    initParallax();
    initBlogFilter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();